import { Component, ChangeDetectionStrategy, inject, computed, model, signal, effect, untracked, input } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { Store } from "@ngrx/store";
import { TranslateModule } from "@ngx-translate/core";

import { DynamicDialogRef } from "primeng/dynamicdialog";
import { ButtonModule } from "primeng/button";
import { DividerModule } from "primeng/divider";
import { ToggleSwitchModule } from "primeng/toggleswitch";

import { ReadyForReviewQuote, QuoteStatus } from "src/api-service";
import { QuoteActions } from "src/app/store/pages/quote/quote.actions";
import { selectQuoteState } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { selectCreditCheckEnabled } from 'src/app/store/application/data/credit-check/quote/quote-credit-check.selectors';
import { selectSalesSupportErrors } from "src/app/store/pages/quote/validation/validation.selectors";

@Component({
    selector: "app-send-order-to-review-dialog",
    templateUrl: "./send-order-to-review-dialog.component.html",
    styleUrls: ["./send-order-to-review-dialog.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FormsModule,
        ButtonModule,
        DividerModule,
        ToggleSwitchModule,
        TranslateModule,
    ],
})
export class SendOrderToReviewDialogComponent {

    // =========================================================================
    // DEPENDENCY INJECTION
    // =========================================================================
    private readonly ref = inject(DynamicDialogRef, { optional: true });

    /**
     * True when rendered as a panel inside a flow stepper rather than as a standalone dialog.
     * The stepper then owns navigation and closing, so this component hides its own footer and
     * stops closing the (absent) dialog ref.
     */
    public readonly embedded = input(false);

    private readonly store = inject(Store);

    // =========================================================================
    // STORE SIGNALS
    // =========================================================================
    public readonly quote = toSignal(this.store.select(selectQuoteState));
    public readonly selectedVersion = toSignal(this.store.select(selectVersion));
    public readonly salesSupportErrors = toSignal(this.store.select(selectSalesSupportErrors), { initialValue: [] });
    private readonly creditCheckEnabled = toSignal(this.store.select(selectCreditCheckEnabled), { initialValue: false });

    // =========================================================================
    // LOCAL STATE
    // =========================================================================
    public readonly creditCheckOK = model(false);

    public readonly toCentralSalesSupport = signal(false);
    private readonly initialized = signal(false);

    // =========================================================================
    // COMPUTED
    // =========================================================================
    public readonly showCreditCheck = computed(() => {
        const quote = this.quote();
        if (!quote) return false;
        // See create-order-dialog: only shown where the real credit-check flow is unavailable.
        // Where it exists, the step dialog before this one carries the attestation.
        if (this.creditCheckEnabled()) return false;
        return quote.deliveryInfo?.paymentTerm?.requiresCreditCheck === true
            && quote.status === QuoteStatus.Sent;
    });

    public readonly hasValidationErrors = computed(() =>
        this.salesSupportErrors().length > 0
    );

    public readonly canConfirm = computed(() => {
        if (this.showCreditCheck() && !this.creditCheckOK()) {
            return false;
        }
        if (this.hasValidationErrors()) {
            return false;
        }
        return true;
    });

    // =========================================================================
    // EFFECTS
    // =========================================================================
    private readonly initEffect = effect(() => {
        const quote = this.quote();
        if (quote && !untracked(() => this.initialized())) {
            this.toCentralSalesSupport.set(
                quote.salesInfo?.responsibleSalesPerson?.defaultUseCentralSalesSupport ?? false
            );
            this.initialized.set(true);
        }
    });

    // =========================================================================
    // PUBLIC METHODS
    // =========================================================================
    public onConfirm(): void {
        const quote = this.quote();
        const version = this.selectedVersion();
        if (!quote || !version) return;

        const request: ReadyForReviewQuote = {
            sendMail: false,
            versionId: version.id,
            toCentralSalesSupport: this.toCentralSalesSupport(),
            subject: null,
            ccEmails: null,
            comment: null,
            message: null,
            recipientEmail: null,
        };

        this.store.dispatch(
            QuoteActions.updateToReadyForReview({
                quoteId: quote.id,
                request: request,
                dialogKey: 'READY_FOR_REVIEW',
            }),
        );
        this.ref?.close();
    }

    public onCancel(): void {
        this.ref?.close();
    }
}
